import { SupportedLanguages } from "@/enums/internationalization";
import { CALENDAR_APPLICATION, CAMPAIGN_VERSION, GPT_VERSION, THEME } from "@/enums/profile";
import { SubscriptionStatus } from "@/enums/stripe";
import { SdkDashboardId } from "@metabase/embedding-sdk-react";
import { RequireAtLeastOne } from "./common";
import { UUID } from "./common/ids";
import { DataExtractionItem } from "./data";

export type Address = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

export type Owner = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

export type OrgServicesAdmin = {
  auth0OrganizationId: string | undefined;
  salesforceId: string | undefined;
  stripeCustomerId: string | undefined;
  twilioAccountSid: string | undefined;
  twilioAuthToken: string | undefined;
  twilioPhoneNumber: string;
  twilioVerifiedPhoneNumber: string | undefined;
  calendarTeamId: string | undefined;
  calendarDefaultEventId: string | undefined;
  calendarEventSlug: string | undefined;
  // Note: calendarEventTypes has been removed
  // Event type to script mapping is now stored directly on scripts via script.calEventTypeId
  alternatePhoneNumbers: string[];
};

type OrgServicesAdminRequest = Omit<
  OrgServicesAdmin,
  "auth0OrganizationId" | "twilioAccountSid" | "twilioAuthToken" | "twilioPhoneNumber"
>;

type OrgServices = Omit<OrgServicesAdmin, "twilioAuthToken">;

export type OrgFeatures = {
  campaignVersion: string;
  calendarApplication: CALENDAR_APPLICATION;
  gptVersion: string;
  useScriptToolAssociations?: boolean;
  /** Missing / empty / v1 = pre-migrated. Do not default getDefaultOrg() to v2. */
  scriptsApiVersion?: string;
};

export type OrgCreateAdmin = {
  address: Address;
  localization: string;
  name: string;
  owner: Owner;
  isDemo: boolean;
  services: Partial<OrgServicesAdmin>;
  subscriptionStatus: SubscriptionStatus;
  details: any;
  products?: OrgProduct[];
  features: OrgFeatures;
};

export type ScrapedContact = {
  name: string;
  value: string;
  type: string;
  channel: "phone" | "email";
};

export type ScrapedFaq = {
  question: string;
  answer: string;
};

export type OrgCreate = {
  address?: Address | null;
  localization: string;
  name: string;
  owner: Owner;
  details: {
    website: string;
    description?: string;
    industries?: string[];
    services?: string[];
    industryTag?: string;
    keyProducts?: string;
    targetMarket?: string;
    gailCanDo?: string[];
    companySize?: string;
    headquarters?: string;
    scrapedContacts?: ScrapedContact[];
    businessHours?: string;
    scrapedFaqs?: ScrapedFaq[];
  };
};

export type OrgSystemPrompt = {
  role: string;
  prompt: string;
  description?: string;
};

export type OrgProduct = {
  name?: string;
  productOffering?: string;
  seats: number;
  subscriptionStatus: SubscriptionStatus;
};

export type UnavailabilityPeriod = UnavailabilityPeriodRequest & {
  id: string;
};

export type UnavailabilityPeriodRequest = {
  reason: string;
  startDate: string;
  endDate: string;
  timezone?: string;
  startTime?: string;
  endTime?: string;
  scriptId?: string | null;
};

export type Holiday = {
  name: string;
  startDate: string;
  endDate: string;
};

type OrgReporting = {
  maxSeats?: number;
  collectionId?: number;
  defaultDashboardId?: SdkDashboardId;
};

export type LegalNameCheckStatus =
  | "pending_scrape"
  | "matched"
  | "mismatch"
  | "started"
  | "failed"
  | "start_failed"
  | "skipped";

export type OrgLegalNameCheck = {
  status: LegalNameCheckStatus;
  submittedName: string;
  websiteName: string | null;
  checkedAt: string;
  missingFields?: string[];
  messageKey?: string;
};

export type OrgOnboardingStatus = "not_started" | "in_progress" | "completed" | "skipped";

export type OrgOnboardingDetails = {
  status: OrgOnboardingStatus;
  maxCompletedStep: number;
  lastViewedStep?: string;
  completedAt?: string | null;
  updatedAt?: string;
  scriptSetupCompleted?: boolean;
  gailFocus?: string[];
  outboundSetupCompleted?: boolean;
  outboundCampaignId?: string;
  outboundScriptId?: string;
  campaignPurpose?: string;
  gailGptAssistantId?: string;
  afterHoursOption?: "voicemail" | "continue" | "custom";
  afterHoursScriptId?: string;
  afterHoursCustomInstructions?: string;
  businessHoursScriptId?: string;
  smartSetupEnhancementsAppliedAt?: string;
  smartSetupEnhancementsResult?: {
    faqCount: number;
    directoryContactCount: number;
  };
};

export type Org = {
  id: UUID;
  address: Address;
  createdAt: string;
  lastModifiedAt: string;
  localization: string;
  name: string;
  owner: Owner;
  services: OrgServices;
  subscriptionStatus: SubscriptionStatus;
  isDemo: boolean;
  features: OrgFeatures;
  details?: {
    systemPrompts?: OrgSystemPrompt[];
    reporting?: OrgReporting;
    onboarding?: OrgOnboardingDetails;
    legalNameCheck?: OrgLegalNameCheck;
    twilioBusinessType?: string;
    businessId?: string;
    businessIdType?: "EIN" | "CBN";
    [key: string]: any;
  };
  products: OrgProduct[];
  unavailabilityPeriod: UnavailabilityPeriod[];
  smsAssistantId?: string;
  maxConcurrentCalls?: number | null;
  dataExtractionDataFields: DataExtractionItem[] | null;
};

export type OrgAdmin = Omit<Org, "services"> & {
  services: OrgServicesAdmin;
  adminGrantStatus?: "written" | "pending" | "failed" | null;
  adminGrantError?: string | null;
};

const getDefaultAddress = (): Address => {
  return {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  };
};

const getDefaultOwner = (): Owner => {
  return {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
  };
};

const getDefaultServices = (): OrgServices => {
  return {
    auth0OrganizationId: "",
    salesforceId: "",
    stripeCustomerId: "",
    twilioAccountSid: "",
    twilioPhoneNumber: "",
    twilioVerifiedPhoneNumber: "",
    calendarTeamId: "",
    calendarDefaultEventId: "",
    calendarEventSlug: "",
    alternatePhoneNumbers: [],
  };
};

const getDefaultOnboardingDetails = (): OrgOnboardingDetails => ({
  status: "not_started",
  maxCompletedStep: 1,
});

export const getDefaultOrg = (): Org => {
  return {
    id: undefined,
    address: getDefaultAddress(),
    createdAt: new Date().toISOString(),
    lastModifiedAt: new Date().toISOString(),
    details: {
      onboarding: getDefaultOnboardingDetails(),
    },
    isDemo: false,
    features: {
      campaignVersion: CAMPAIGN_VERSION.V2,
      calendarApplication: CALENDAR_APPLICATION.NONE,
      gptVersion: GPT_VERSION.V2,
      useScriptToolAssociations: false,
    },
    owner: getDefaultOwner(),
    services: getDefaultServices(),
    localization: "en-US",
    name: "",
    subscriptionStatus: SubscriptionStatus.none,
    products: [],
    unavailabilityPeriod: [],
    dataExtractionDataFields: null,
  };
};

export type OrgAdminRequest = RequireAtLeastOne<
  Omit<OrgAdmin, "id" | "createdAt" | "lastModifiedAt" | "services" | "features" | "products"> & {
    services: RequireAtLeastOne<OrgServicesAdminRequest>;
    features: RequireAtLeastOne<OrgFeatures>;
    products: RequireAtLeastOne<OrgProduct[]>;
  }
>;

export type OrgRequest = RequireAtLeastOne<
  Omit<
    Org,
    | "id"
    | "createdAt"
    | "lastModifiedAt"
    | "services"
    | "isDemo"
    | "subscriptionStatus"
    | "features"
  > & {
    features?: RequireAtLeastOne<OrgFeatures>;
  }
>;

/**
 * Trial usage snapshot for the current organization along with the limits
 * it is being measured against. Returned by
 * `GET /.internal/customer/trial-usage-limit`.
 */
export type TrialUsageLimit = {
  totalCallSeconds: number;
  totalConversations: number;
  maxTotalCallSeconds: number;
  maxTotalConversations: number;
  isLimitReached: boolean;
  isEnforced: boolean;
  isTrialing: boolean;
};

export type UserProfile = {
  dashboardLayout: string;
  theme: THEME;
  language?: SupportedLanguages;
  favorites: Record<string, string[]>;
  columns: Record<string, string[]>;
  isDefaultProfile?: boolean;
};
